import React, { useEffect, useState, useCallback, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import SearchSection from '../GeneracionCargosManualesScreen/SearchSection';
import StudentTable from '../GeneracionCargosManualesScreen/StudentTable';
import LoaderOverlay from '../../components/LoaderOverlay';
import styles from '../../styles/GeneracionCargosManualesStyles/GeneracionCargosManuales.module.css';
import { getEstudiantesCampus, getCampusDropdown } from '../../api/cargosManuales';
import { generateConstanciaPDF } from '../../utils/generateConstanciaPDF';
import { generateKardexPDF } from '../../utils/generateKardexPDF';
import { generateCertificadoPDF } from '../../utils/generateCertificadoPDF';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

// ---------- helpers ----------
function extractTokens(html) {
  const matches = [...(html || '').matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g)];
  return [...new Set(matches.map((m) => m[1].trim()))];
}

// ---------- Modal de Generación de Constancia ----------
function ModalConstancia({ student, onClose }) {
  // Paso 1: selección · Paso 2: campos manuales + generar
  const [step, setStep] = useState(1);
  const [constancias, setConstancias] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [loadingConstancias, setLoadingConstancias] = useState(true);
  const [selectedConstancia, setSelectedConstancia] = useState(null);
  const [manualEtiquetas, setManualEtiquetas] = useState([]); // etiquetas manuales requeridas
  const [valoresManuales, setValoresManuales] = useState({});
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const overlayRef = useRef(null);

  // Cargar constancias y catálogo de etiquetas al montar
  useEffect(() => {
    const fetchAll = async () => {
      setLoadingConstancias(true);
      try {
        const [resC, resE] = await Promise.all([
          fetch(`${API_BASE}/escolar/constancias/`),
          fetch(`${API_BASE}/escolar/etiquetas/`),
        ]);
        const dataC = await resC.json();
        const dataE = await resE.json();
        const listC = Array.isArray(dataC) ? dataC : dataC.results || [];
        const listE = Array.isArray(dataE) ? dataE : dataE.results || [];
        setConstancias(listC.filter((c) => c.activo !== false && c.activo !== 0));
        setEtiquetas(listE.filter((e) => e.activo !== false && e.activo !== 0));
      } catch {
        setConstancias([]);
        setEtiquetas([]);
      } finally {
        setLoadingConstancias(false);
      }
    };
    fetchAll();
  }, []);

  const handleSelectConstancia = (c) => {
    setSelectedConstancia(c);
    const tokens = extractTokens(c.redaccion || '');
    // Etiqueta manual = tabla_origen 'manual' Y su campo_origen aparece como token
    const etiquetasPorCampo = {};
    etiquetas.forEach((e) => {
      etiquetasPorCampo[(e.campo_origen || '').toLowerCase()] = e;
      etiquetasPorCampo[(e.nombre || '').toLowerCase()] = e;
    });
    const manuales = tokens
      .map((t) => etiquetasPorCampo[t.toLowerCase()])
      .filter((e) => e && (e.tabla_origen || '').toLowerCase() === 'manual')
      // deduplicar
      .filter((e, i, arr) => arr.findIndex((x) => x.id_etiqueta === e.id_etiqueta) === i);

    setManualEtiquetas(manuales);
    const init = {};
    manuales.forEach((e) => {
      if (e.tipo_dato === 'fecha' && e.usar_fecha_actual) {
        init[e.campo_origen] = new Date().toISOString().slice(0, 10);
      } else {
        init[e.campo_origen] = '';
      }
    });
    setValoresManuales(init);
    setGenError('');
    setStep(2);
  };

  const handleGenerar = async () => {
    setGenError('');
    // Validar campos manuales requeridos
    const faltantes = manualEtiquetas.filter((e) => {
      if (e.tipo_dato === 'fecha' && e.usar_fecha_actual) {
        return false;
      }
      return !(valoresManuales[e.campo_origen] || '').trim();
    });
    if (faltantes.length > 0) {
      setGenError(`Completa los campos requeridos: ${faltantes.map((e) => e.nombre).join(', ')}`);
      return;
    }

    setGenerating(true);
    try {
      const res = await fetch(
        `${API_BASE}/escolar/constancias/${selectedConstancia.id_constancia}/render/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_estudiante: student.id_estudiante || student.id,
            valores_manuales: valoresManuales,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        setGenError(err?.error || 'Error al generar la constancia.');
        return;
      }
      const data = await res.json();
      await generateConstanciaPDF({
        redaccionHtml: data.redaccion_renderizada,
        nombreConstancia: selectedConstancia.nombre_constancia,
        claveConstancia: selectedConstancia.clave_constancia,
        nombreEstudiante: student.nombre || student.nombre_completo || '',
        matricula: student.matricula || '',
        descargar: true,
      });
      onClose();
    } catch (e) {
      setGenError('Error de conexión al generar la constancia.');
    } finally {
      setGenerating(false);
    }
  };

  // Estilos inline para el modal (no requiere nuevo .css)
  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3000,
  };
  const cardStyle = {
    background: '#fff', borderRadius: 10, width: 560, maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  };
  const headerStyle = {
    background: '#1a3764', color: '#fff', padding: '16px 20px',
    borderRadius: '10px 10px 0 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  };
  const bodyStyle = { padding: 24, overflowY: 'auto', flex: 1 };
  const footerStyle = {
    padding: '14px 20px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  };
  const btnPrimary = {
    background: '#1a3764', color: '#fff', border: 'none', borderRadius: 6,
    padding: '9px 22px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  };
  const btnSecondary = {
    background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6,
    padding: '9px 18px', fontSize: 13, cursor: 'pointer',
  };
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db',
    borderRadius: 6, padding: '8px 10px', fontSize: 13, marginTop: 4,
    fontFamily: 'inherit',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', letterSpacing: '0.4px', textTransform: 'uppercase' };

  return (
    <div style={overlayStyle} ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Generar Constancia</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
              {student.nombre || student.nombre_completo} — {student.matricula}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Paso 1: Seleccionar constancia */}
        {step === 1 && (
          <>
            <div style={bodyStyle}>
              {loadingConstancias ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0' }}>Cargando constancias…</div>
              ) : constancias.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0' }}>
                  No hay constancias registradas.
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                    Selecciona la constancia a generar:
                  </div>
                  {constancias.map((c) => (
                    <button
                      key={c.id_constancia}
                      type="button"
                      onClick={() => handleSelectConstancia(c)}
                      style={{
                        display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left',
                        background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7,
                        padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
                        transition: 'border-color .15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1a3764')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e5e7eb')}
                    >
                      <span style={{ fontWeight: 700, color: '#1a3764', fontSize: 13 }}>
                        {c.nombre_constancia}
                      </span>
                      <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                        Clave: {c.clave_constancia}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={footerStyle}>
              <button type="button" style={btnSecondary} onClick={onClose}>Cancelar</button>
            </div>
          </>
        )}

        {/* Paso 2: Campos manuales + Generar */}
        {step === 2 && selectedConstancia && (
          <>
            <div style={bodyStyle}>
              {/* Info de la constancia seleccionada */}
              <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: '#1a3764' }}>{selectedConstancia.nombre_constancia}</span>
                <span style={{ color: '#6b7280', marginLeft: 10 }}>{selectedConstancia.clave_constancia}</span>
              </div>

              {manualEtiquetas.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 12 }}>
                  Esta constancia no requiere campos adicionales. Puedes generarla directamente.
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: '#374151', marginBottom: 14, fontWeight: 600 }}>
                    Completa los siguientes campos:
                  </div>
                  {manualEtiquetas.map((e) => (
                    <div key={e.id_etiqueta} style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>
                        {e.nombre}
                        {!(e.tipo_dato === 'fecha' && e.usar_fecha_actual) && <span style={{ color: '#ef4444' }}> *</span>}
                      </label>
                      {e.tipo_dato === 'texto' || e.tipo_dato === 'decimal' || e.tipo_dato === 'numero' ? (
                        e.descripcion && e.descripcion.length > 60 ? (
                          <textarea
                            rows={3}
                            style={{ ...inputStyle, resize: 'vertical' }}
                            value={valoresManuales[e.campo_origen] || ''}
                            onChange={(ev) => setValoresManuales((prev) => ({ ...prev, [e.campo_origen]: ev.target.value }))}
                            placeholder={e.descripcion || ''}
                          />
                        ) : (
                          <input
                            type={e.tipo_dato === 'numero' || e.tipo_dato === 'decimal' ? 'number' : 'text'}
                            style={inputStyle}
                            value={valoresManuales[e.campo_origen] || ''}
                            onChange={(ev) => setValoresManuales((prev) => ({ ...prev, [e.campo_origen]: ev.target.value }))}
                            placeholder={e.descripcion || ''}
                          />
                        )
                      ) : e.tipo_dato === 'fecha' ? (
                        <input
                          type="date"
                          style={inputStyle}
                          value={valoresManuales[e.campo_origen] || ''}
                          onChange={(ev) => setValoresManuales((prev) => ({ ...prev, [e.campo_origen]: ev.target.value }))}
                          disabled={Boolean(e.usar_fecha_actual)}
                        />
                      ) : e.tipo_dato === 'booleano' ? (
                        <select
                          style={inputStyle}
                          value={valoresManuales[e.campo_origen] || ''}
                          onChange={(ev) => setValoresManuales((prev) => ({ ...prev, [e.campo_origen]: ev.target.value }))}
                        >
                          <option value="">— Selecciona —</option>
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          style={inputStyle}
                          value={valoresManuales[e.campo_origen] || ''}
                          onChange={(ev) => setValoresManuales((prev) => ({ ...prev, [e.campo_origen]: ev.target.value }))}
                          placeholder={e.descripcion || ''}
                        />
                      )}
                      {e.descripcion && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{e.descripcion}</div>
                      )}
                      {e.tipo_dato === 'fecha' && e.usar_fecha_actual && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                          Se usara la fecha actual automaticamente al generar.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {genError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 14px', color: '#b91c1c', fontSize: 13, marginTop: 8 }}>
                  {genError}
                </div>
              )}
            </div>
            <div style={footerStyle}>
              <button type="button" style={btnSecondary} onClick={() => { setStep(1); setGenError(''); }}>
                ← Atrás
              </button>
              <button
                type="button"
                style={{ ...btnPrimary, opacity: generating ? 0.7 : 1, cursor: generating ? 'not-allowed' : 'pointer' }}
                onClick={handleGenerar}
                disabled={generating}
              >
                {generating ? 'Generando…' : '⬇ Generar PDF'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Modal de Generación de Kardex ----------
function ModalKardex({ student, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kardexData, setKardexData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const fetchKardex = async () => {
      setLoading(true);
      setError('');
      try {
        const id = student.id_estudiante || student.id;
        // Endpoint oficial del Kardex: Plan de Estudios + esc_cardex
        const res = await fetch(`${API_BASE}/escolar/kardex/${id}/`);
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          setError(err?.error || 'Error al obtener el kardex.');
          return;
        }
        const data = await res.json();
        setKardexData(data);
      } catch {
        setError('Error de conexión al obtener el kardex.');
      } finally {
        setLoading(false);
      }
    };
    fetchKardex();
  }, [student]);

  const handleGenerar = async () => {
    if (!kardexData) return;
    setGenerating(true);
    try {
      await generateKardexPDF({
        encabezado: kardexData.encabezado,
        materias: kardexData.materias || [],
        resumen: kardexData.resumen || {},
        descargar: true,
      });
      onClose();
    } catch (e) {
      setError('Error al generar el PDF del kardex.');
    } finally {
      setGenerating(false);
    }
  };

  // El total lo tomamos del resumen del servidor (incluye materias sin calificación)
  const resumen = kardexData?.resumen || {};
  const totalMaterias = resumen.total_materias_plan
    || (kardexData?.ciclos || []).reduce((acc, c) => acc + (c.materias?.length || 0), 0);

  // Estilos inline
  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3000,
  };
  const cardStyle = {
    background: '#fff', borderRadius: 10, width: 520, maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  };
  const headerStyle = {
    background: '#1a3764', color: '#fff', padding: '16px 20px',
    borderRadius: '10px 10px 0 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  };
  const bodyStyle = { padding: 24, overflowY: 'auto', flex: 1 };
  const footerStyle = {
    padding: '14px 20px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  };
  const btnPrimary = {
    background: '#1a3764', color: '#fff', border: 'none', borderRadius: 6,
    padding: '9px 22px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  };
  const btnSecondary = {
    background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6,
    padding: '9px 18px', fontSize: 13, cursor: 'pointer',
  };

  return (
    <div style={overlayStyle} ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Generar Kardex</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
              {student.nombre || student.nombre_completo} — {student.matricula}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0' }}>Cargando kardex…</div>
          ) : error ? (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '12px 16px', color: '#b91c1c', fontSize: 13 }}>
              {error}
            </div>
          ) : kardexData ? (
            <div>
              {/* Resumen */}
              <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#1a3764', fontSize: 13, marginBottom: 6 }}>
                  Historial Académico
                </div>
                <div style={{ fontSize: 12, color: '#374151', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                  <span><strong>Programa:</strong> {kardexData.encabezado.programa}</span>
                  <span><strong>Campus:</strong> {kardexData.encabezado.campus}</span>
                  <span><strong>Materias del plan:</strong> {resumen.total_materias_plan ?? '—'}</span>
                  <span><strong>Aprobadas:</strong> {resumen.materias_aprobadas ?? '—'}</span>
                  <span><strong>Créditos obtenidos:</strong> {resumen.creditos_obtenidos ?? '—'} / {resumen.creditos_totales ?? '—'}</span>
                  <span><strong>Pendientes:</strong> {resumen.materias_pendientes ?? '—'}</span>
                </div>
              </div>

              {/* Preview consolidado por materia (misma fuente que el PDF) */}
              {(kardexData.materias || []).filter(m => m.estatus !== 'SIN_CURSAR').length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                  No hay calificaciones registradas aún. El PDF incluirá todas las materias del plan.
                </div>
              ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  {(kardexData.materias || [])
                    .filter(m => m.estatus !== 'SIN_CURSAR')
                    .map((m) => {
                      const estatusLabel = { APROBADA: 'Aprobada', REPROBADA: 'Reprobada', EN_CURSO: 'En curso' }[m.estatus] || m.estatus;
                      const estatusColor = m.estatus === 'APROBADA' ? '#166534' : m.estatus === 'REPROBADA' ? '#b91c1c' : '#6b7280';
                      const intentos = (m.oportunidades || []).map((o) => {
                        const cal = (o.calificacion !== null && o.calificacion !== undefined) ? o.calificacion : '—';
                        return `${o.nombre_ciclo}: ${cal}`;
                      });
                      if (m.revalidacion !== null && m.revalidacion !== undefined) {
                        intentos.push(`Rev.: ${m.revalidacion}`);
                      }
                      return (
                        <div key={m.id_materia} style={{ padding: '6px 12px', borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11 }}>
                            <span style={{ fontWeight: 600, color: '#374151' }}>{m.materia_nombre}</span>
                            <span style={{ color: estatusColor, fontWeight: 700, whiteSpace: 'nowrap' }}>{estatusLabel}</span>
                          </div>
                          <div style={{ color: '#6b7280', fontSize: 10, marginTop: 2 }}>
                            {intentos.length ? intentos.join('  ·  ') : 'Sin intentos con calificación'}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" style={btnSecondary} onClick={onClose}>Cancelar</button>
          <button
            type="button"
            style={{ ...btnPrimary, opacity: (generating || loading || !!error || !kardexData || totalMaterias === 0) ? 0.6 : 1, cursor: (generating || loading || !!error || !kardexData || totalMaterias === 0) ? 'not-allowed' : 'pointer' }}
            onClick={handleGenerar}
            disabled={generating || loading || !!error || !kardexData}
          >
            {generating ? 'Generando…' : '⬇ Descargar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Modal de Generación de Certificado ----------
function ModalCertificado({ student, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [certData, setCertData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const id = student.id_estudiante || student.id;
        const res = await fetch(`${API_BASE}/escolar/calificaciones/boleta/${id}/`);
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          setError(err?.error || 'Error al obtener los datos del certificado.');
          return;
        }
        const data = await res.json();
        setCertData(data);
      } catch {
        setError('Error de conexión al obtener los datos.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [student]);

  const handleGenerar = async () => {
    if (!certData) return;
    setGenerating(true);
    try {
      await generateCertificadoPDF({
        encabezado: certData.encabezado,
        ciclos: certData.ciclos || [],
        descargar: true,
      });
      onClose();
    } catch {
      setError('Error al generar el PDF del certificado.');
    } finally {
      setGenerating(false);
    }
  };

  const totalMaterias = certData
    ? (certData.ciclos || []).reduce((acc, c) => acc + (c.materias?.length || 0), 0)
    : 0;

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3000,
  };
  const cardStyle = {
    background: '#fff', borderRadius: 10, width: 520, maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  };
  const headerStyle = {
    background: '#1a3764', color: '#fff', padding: '16px 20px',
    borderRadius: '10px 10px 0 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  };
  const bodyStyle = { padding: 24, overflowY: 'auto', flex: 1 };
  const footerStyle = {
    padding: '14px 20px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  };
  const btnPrimary = {
    background: '#1a3764', color: '#fff', border: 'none', borderRadius: 6,
    padding: '9px 22px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  };
  const btnSecondary = {
    background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6,
    padding: '9px 18px', fontSize: 13, cursor: 'pointer',
  };
  const disabled = generating || loading || !!error || !certData || totalMaterias === 0;

  return (
    <div style={overlayStyle} ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Generar Certificado de Estudios</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
              {student.nombre || student.nombre_completo} — {student.matricula}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>
            ×
          </button>
        </div>

        <div style={bodyStyle}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0' }}>Cargando datos…</div>
          ) : error ? (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '12px 16px', color: '#b91c1c', fontSize: 13 }}>
              {error}
            </div>
          ) : certData ? (
            <div>
              <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#1a3764', fontSize: 13, marginBottom: 6 }}>Resumen del Certificado</div>
                <div style={{ fontSize: 12, color: '#374151', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                  <span><strong>Programa:</strong> {certData.encabezado.programa}</span>
                  <span><strong>Campus:</strong> {certData.encabezado.campus}</span>
                  <span><strong>Ciclos:</strong> {(certData.ciclos || []).length}</span>
                  <span><strong>Materias:</strong> {totalMaterias}</span>
                </div>
              </div>
              {totalMaterias === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                  No hay asignaturas registradas para este estudiante.
                </div>
              ) : (
                <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  {[...(certData.ciclos || [])].sort((a, b) => a.id_ciclo_escolar - b.id_ciclo_escolar).map((ciclo) => (
                    <div key={ciclo.id_ciclo_escolar}>
                      <div style={{ background: '#e6edf8', padding: '5px 12px', fontSize: 11, fontWeight: 700, color: '#1a3764', borderBottom: '1px solid #d1dff7' }}>
                        {ciclo.nombre_ciclo} — {ciclo.materias?.length || 0} materia(s)
                      </div>
                      {(ciclo.materias || []).slice(0, 4).map((m, i) => (
                        <div key={i} style={{ padding: '3px 12px', fontSize: 11, color: '#374151', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}>
                          <span>{m.materia_nombre}</span>
                          <span style={{ color: m.final !== null && m.final < 6 ? '#b91c1c' : '#166534', fontWeight: 600 }}>
                            {m.final !== null ? `Final: ${m.final}` : m.parcial_1 !== null ? `P1: ${m.parcial_1}` : '—'}
                          </span>
                        </div>
                      ))}
                      {(ciclo.materias?.length || 0) > 4 && (
                        <div style={{ padding: '3px 12px', fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>
                          +{ciclo.materias.length - 4} más…
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div style={footerStyle}>
          <button type="button" style={btnSecondary} onClick={onClose}>Cancelar</button>
          <button
            type="button"
            style={{ ...btnPrimary, opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
            onClick={handleGenerar}
            disabled={disabled}
          >
            {generating ? 'Generando…' : '⬇ Descargar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Pantalla principal ----------
function TramitesEscolaresScreen() {
  const [searchTerm, setSearchTerm] = useState('');
  const [campus, setCampus] = useState(() => localStorage.getItem('selectedCampus') || '');
  const [campusIdState, setCampusIdState] = useState(null);
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pageSize = 8;

  // Modales
  const [modalStudent, setModalStudent] = useState(null);
  const [kardexStudent, setKardexStudent] = useState(null);
  const [certificadoStudent, setCertificadoStudent] = useState(null);

  useEffect(() => {
    const handleStorageChange = () => {
      const newCampus = localStorage.getItem('selectedCampus') || '';
      setCampus(newCampus);
    };
    const handleCampusChanged = (e) => {
      const newCampus = e.detail?.campus || localStorage.getItem('selectedCampus') || '';
      setCampus(newCampus);
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('campusChanged', handleCampusChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('campusChanged', handleCampusChanged);
    };
  }, []);

  const loadStudents = useCallback(async (campusId, search = '', pageToLoad = 1, showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const response = await getEstudiantesCampus(
        (!campusId || campusId === 'all' || campusId === 'Todos' || campusId === 'Todos los Campus') ? 'all' : campusId,
        search, pageToLoad, pageSize
      );
      if (response.success) {
        const pageStudents = response.estudiantes || [];
        setStudents(pageStudents);
        setTotalCount(response.count || pageStudents.length);
        setPage(response.page || pageToLoad);
        setTotalPages(response.total_pages || Math.max(1, Math.ceil((response.count || pageStudents.length) / pageSize)));
      } else {
        setStudents([]); setTotalCount(0); setTotalPages(1);
        setError('No se pudo cargar el listado de estudiantes.');
      }
    } catch {
      setStudents([]); setTotalCount(0); setTotalPages(1);
      setError('Error al buscar estudiantes. Intente nuevamente.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!campus) { setStudents([]); setTotalCount(0); setTotalPages(1); return; }
    const fetchAndLoad = async () => {
      try {
        const campusList = await getCampusDropdown();
        if (campusList && Array.isArray(campusList)) localStorage.setItem('campusDropdownCache', JSON.stringify(campusList));
        const campusObj = campusList.find((c) => c.nombre === campus || c.nombre_campus === campus || c.id === campus);
        if (campusObj?.id) { setCampusIdState(campusObj.id); loadStudents(campusObj.id, searchTerm, 1); }
        else { setStudents([]); setTotalCount(0); setTotalPages(1); setCampusIdState(null); }
      } catch { setStudents([]); setTotalCount(0); setTotalPages(1); setCampusIdState(null); setError('No se pudo cargar la configuración del campus.'); }
    };
    fetchAndLoad();
  }, [campus]);

  useEffect(() => {
    const handler = setTimeout(() => { if (campusIdState) loadStudents(campusIdState, searchTerm, 1, false); }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, campusIdState]);

  const handleSearch = async () => { if (campusIdState) await loadStudents(campusIdState, searchTerm, 1); };

  const tramiteActions = [
    { key: 'constancia', label: 'Generar Constancia' },
    { key: 'kardex',     label: 'Generar Kardex' },
    { key: 'certificado', label: 'Generar Certificado' },
  ];

  const handleTramiteAction = (action, student) => {
    if (action.key === 'constancia') {
      setModalStudent(student);
    } else if (action.key === 'kardex') {
      setKardexStudent(student);
    } else if (action.key === 'certificado') {
      setCertificadoStudent(student);
    }
  };

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}><Sidebar /></div>
      <div className={styles.contentColumn}>
        <div className={styles.contentArea}>
          <HeaderGlobal title="Trámites Escolares" />
          <div className={styles.contentWrapper} style={{ position: 'relative', minHeight: 'calc(100vh - 64px)', height: 'auto' }}>
            <LoaderOverlay visible={loading} label="Cargando estudiantes..." />
            <SearchSection searchTerm={searchTerm} onSearchChange={setSearchTerm} onSearch={handleSearch} loading={loading} />
            {error && <div className={styles.errorMessage}>{error}</div>}
            <StudentTable
              students={students}
              onSelectStudent={() => {}}
              serverPaging={true}
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              actions={tramiteActions}
              onAction={handleTramiteAction}
              onPageChange={(p) => { if (campusIdState) loadStudents(campusIdState, searchTerm, p); }}
            />
          </div>
        </div>
      </div>

      {/* Modal de generación de constancia */}
      {modalStudent && (
        <ModalConstancia
          student={modalStudent}
          onClose={() => setModalStudent(null)}
        />
      )}

      {/* Modal de generación de kardex */}
      {kardexStudent && (
        <ModalKardex
          student={kardexStudent}
          onClose={() => setKardexStudent(null)}
        />
      )}

      {/* Modal de generación de certificado */}
      {certificadoStudent && (
        <ModalCertificado
          student={certificadoStudent}
          onClose={() => setCertificadoStudent(null)}
        />
      )}
    </div>
  );
}

export default TramitesEscolaresScreen;
